import { useEffect, useState } from "react";
import { Utils, fetchWithAuth, isFileHeaderFormat, templateHeaderFormat } from "../utils";

/**
 * For a WhatsApp template with an image/video/document header: asks the backend whether the media saved
 * with the template can be sent as is (downloaded completely) so pickers can block a send that would
 * go out with a cut-off image.
 *
 * @param {Object} opts
 * @param {string|number} opts.appId
 * @param {Object|null} opts.template - template row ({ name, language, components })
 * @param {boolean} [opts.skip] - true when the user already supplied their own media (no check needed)
 * @returns {{ status: "idle"|"checking"|"ok"|"bad", reason: string }}
 */
export function useWhatsAppHeaderCheck({ appId, template, skip = false }) {
  const [state, setState] = useState({ status: "idle", reason: "" });
  const name = template?.name || template?.text || "";
  const language = template?.language || "";
  const needsMedia = isFileHeaderFormat(templateHeaderFormat(template));

  useEffect(() => {
    if (!appId || !name || !needsMedia || skip) {
      setState({ status: "idle", reason: "" });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "checking", reason: "" });
    fetchWithAuth(Utils.baseurl + "application/whatsapp/templates/header-check", {
      method: "POST",
      body: JSON.stringify({ app_id: appId, name, language }),
    })
      .then((res) => res.json().catch(() => ({})))
      .then((result) => {
        if (cancelled) return;
        if (result?.status && result.data?.ok) {
          setState({ status: "ok", reason: "" });
        } else {
          setState({
            status: "bad",
            reason: result?.data?.reason || result?.message || "Could not check the saved header media.",
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: "bad", reason: "Could not check the saved header media." });
      });
    return () => {
      cancelled = true;
    };
  }, [appId, name, language, needsMedia, skip]);

  return state;
}
